<!-- eslint-disable vue/no-v-html -->
<!-- eslint-disable vue/first-attribute-linebreak -->
<template>
  <div class="registration-form__privacy-policy">
    <a class="registration-form__privacy-policy-link"
       href="/home/privacy-policy"
       target="_blank" 
       rel="noopener noreferrer">Privacy policy</a>
  </div>
  <div class="registration-form">
    <UtdOptionsCard class="primary-card">
      <template #content>
        <div class="registration-form__container">
          <div class="registration-form__inner-container">
            <form @submit.prevent="handleFormSubmit">
              <UtdNotification v-if="isServerErrorNotificationVisible"
                               id="serverErrorNotification"
                               :has-close-notification="true"
                               notification-mode="error"
                               @closed="isServerErrorNotificationVisible = false">
                <template #title>
                  <h4>Submission Error</h4>
                </template>
                <div class="registration-form__server-error-notification">
                  <ul>
                    <li v-for="(error, key) in serverError.general" 
                        :key="key"
                        v-html="error" />
                  </ul>
                </div>
              </UtdNotification>
              <RegistrationFormBasicInfo @select-change="handleSelectChange"
                                         @merge-clicked="cancelRegistration" />
              <RegistrationFormZipCityState @select-change="handleSelectChange" />
              <RegistrationFormPracticeType @select-change="handleSelectChange" />
              <RegistrationFormUsernamePassword 
                v-show="isShowUsernamePassword"
                :has-sent-verification-code="hasSentVerificationCode"
                :has-resent-verification-code="hasResentVerificationCode"
                :is-auth-notification-visible="isAuthNotificationVisible"
                :is-auth-error-notification-visible="isAuthErrorNotificationVisible"
                :is-allow-otc-generation="isAllowOtcGeneration"
                :is-submitting="isSubmitting"
                @close-auth-error-notification="isAuthErrorNotificationVisible = false"
                @send-verification-code="handleSendVerificationCode" 
                @submit-form="handleFormSubmit"
              />
              <fieldset class="registration-form__terms-container wk-js2 registration-form-field">
                <UtdCheckbox class="registration-form__terms-checkbox"
                             :checked="form.termsAccepted"
                             :required="true"
                             @checkbox-changed="handleTermsChange">
                  <span class="registration-form__terms-text">
                    I agree to the
                    <a class="registration-form__terms-link"
                       href="https://www.wolterskluwer.com/en/know/clinical-effectiveness-terms" 
                       target="_blank" 
                       rel="noopener noreferrer">Terms of Use</a>
                  </span>
                </UtdCheckbox>
              </fieldset>
            </form>
            <div v-if="isGuestPassUser" class="registration-form__guest-pass-disclaimer">
              <p>
                {{ GUEST_PASS_REGISTRATION_DISCLAIMER }}
              </p>
            </div>
          </div>
        </div>
      </template>
      <template #actions>
        <UtdButton v-if="isCancelButtonVisible"
                   id="cancelButton"
                   button-size="large"
                   button-style="ghost"
                   @click="cancelRegistration">
          Cancel
        </UtdButton>
        <UtdButton v-if="!isRequiresTwoFactorValidation"
                   id="submitButton"
                   :disabled="isSubmitDisabled || isSubmitting"
                   button-size="large"
                   type="submit"
                   @click="handleFormSubmit">
          Submit
        </UtdButton>
        <UtdButton v-else-if="isRequiresTwoFactorValidation && !hasSentVerificationCode" 
                   id="sendVerificationButton"
                   :disabled="isSubmitDisabled || isSubmitting"
                   button-color="green-shade-3"
                   button-size="large"
                   type="submit"
                   @click="handleSendVerificationCode">
          Send Verification Code
        </UtdButton>
        <UtdButton v-else-if="isRequiresTwoFactorValidation && hasSentVerificationCode" 
                   id="submitVerificationButton"
                   :disabled="isSubmitCodeDisabled"
                   button-color="green-shade-3"
                   button-size="large"
                   type="submit"
                   @click="handleFormSubmit">
          Submit Verification Code
        </UtdButton>
      </template>
    </UtdOptionsCard>
  </div>
</template>

<script setup>
import UtdOptionsCard from '_acaSrc/components/shared/stdlib/UtdOptionsCard.vue';
import UtdButton from '_acaSrc/components/shared/stdlib/UtdButton.vue';
import UtdCheckbox from '_acaSrc/components/shared/stdlib/UtdCheckbox.vue';
import { getWindow } from '_acaSrc/utility/DOM';
import useRegistrationForm from '_acaSrc/composables/registrationForm.composable';
import { useStore } from 'vuex';
import { onMounted, ref, computed } from 'vue';
import { C_HTTP,
    C_REG_MERGE,
    GUEST_PASS_LICENSE,
    GUEST_PASS_REGISTRATION_DISCLAIMER,
    REG_FIELD,
    REGISTRATION_DEFAULT_ERROR_MESSAGE,
    REGISTRATION_FIELD_ERROR,
    REGISTRATION_FIELD_ERROR_MESSAGE,
    REGISTRATION_NO_OTC_ENTRY_ERROR_MESSAGE,
    REGISTRATION_OTC_EXIT_BROWSER_ERROR_MESSAGE,
    REGISTRATION_REVIEW_FORM_MESSAGE,
    SALUTATION_OPTIONS
} from '_acaSrc/utility/constants';
import { setQueryParamValue } from '_acaSrc/utility/http';
import utdRest from '_acaSrc/utility/http/UtdRestHooks';
import RegistrationFormUsernamePassword from './RegistrationFormUsernamePassword.vue';
import RegistrationFormBasicInfo from './RegistrationFormBasicInfo.vue';
import RegistrationFormPracticeType from './RegistrationFormPracticeType.vue';
import UtdNotification from '_acaSrc/components/shared/utd/UtdNotification.vue';
import DOMPurify from 'dompurify';
import RegistrationFormZipCityState from './RegistrationFormAddressInfo.vue';

const {
    form,
    isDirty,
    isUSA,
    isUSOrCanada,
    cityDropdownOptions,
    filteredStateOptions,
    needsNewCode,
    zipCodePrefilled,
    isSubmitDisabled,
    sendCodeAttempts,
    resetServerError,
    checkResetServerError,
    serverError,
    setServerError,
    resetForm,
    validateForm,
    checkFieldInvalid,
    resetAllServerErrors
} = useRegistrationForm();

const store = useStore();

const registrationForm = computed(() => store.getters['login/registrationForm']);
const formFields = computed(() => registrationForm.value.formFields);
const isShowUsernamePassword = computed(() =>
    formFields.value['select.user.name.and.password']
    && !formFields.value['select.user.name.and.password']?.isHidden);
const registrationInfo = computed(() => store.getters['login/registrationInfo']);

const isSubmitCodeDisabled = computed(() => 
    isSubmitDisabled.value
    || checkFieldInvalid(REG_FIELD.VERIFICATION_CODE)
    || !isAllowOtcEntry.value
    || needsNewCode.value
    || isSubmitting.value
);

const emit = defineEmits([
    'cancelRegistration',
    'closed'
]);

const stateDropdownList = computed(() => store.getters['account/myAccountStates']);
const handleSelectChange = ({value, name}) => {
    if (name === REG_FIELD.COUNTRY) {
        form.zip = '';
        form.city = '';
        form.state = null;
        isDirty.zip = false;
        isDirty.city = false;
        zipCodePrefilled.value = false;
        cityDropdownOptions.value = [];
        filteredStateOptions.value = stateDropdownList.value;
        resetServerError(REG_FIELD.ZIP);
    }
    form[name] = parseInt(value);
    checkResetServerError();
};

const hasSentVerificationCode = ref(false);
const hasResentVerificationCode = ref(false);
const isAuthNotificationVisible = ref(false);
const isAuthErrorNotificationVisible = ref(false);
const isSubmitting = ref(false);

const handleTermsChange = (isChecked) => {
    form.termsAccepted = isChecked;
    isDirty.termsAccepted = true;
    checkResetServerError(REG_FIELD.TERMS);
};

const setupFormPayload = (inputForm) => {
    const responseForm = {
        salutation: inputForm.salutation,
        firstName: inputForm.firstName,
        lastName: inputForm.lastName,
        suffix: inputForm.suffix,
        email: inputForm.email,
        city: inputForm.city,
        zip: inputForm.zip,
        state: inputForm.state,
        county: inputForm.county,
        address1: inputForm.address1,
        address2: inputForm.address2,
        phone1: inputForm.phone1,
        phone1Type: inputForm.phone1Type,
        phone1Ext: inputForm.phone1Ext,
        country: inputForm.country,
        practiceSetting: inputForm.practiceSetting,
        practiceType: inputForm.practiceType,
        specialty: inputForm.specialty,
        registrationUserName: inputForm.registrationUserName,
        password: inputForm.password,
        verifyPassword: inputForm.verifyPassword
    };
    transformDropdownValues(responseForm);
    setGuestPassUserFields(responseForm);
    return responseForm;
};

const isGuestPassUser = computed(() => store.getters['footer/licensee'] === GUEST_PASS_LICENSE
  || store.getters['profile/userProfileVisitor'].isGuestUser
  || registrationForm.value.formName === C_REG_MERGE.GUEST_PASS_USER_FORM);
const isCancelButtonVisible = computed(() => {
    return !isGuestPassUser.value;
});
const setGuestPassUserFields = (responseForm) => {
    if (isGuestPassUser.value) {
        responseForm.registrationUserName = 
          store.getters['profile/userProfileVisitor'].userName || '';
    }
};

const transformDropdownValues = (responseForm) => {
    const selectedCountryIdx = parseInt(form.country);
    const formattedCountry = store.getters['account/myAccountCountries'][selectedCountryIdx]?.value 
        || '';
    responseForm.country = formattedCountry;

    const selectedSalutationIdx = parseInt(form.salutation);
    const formattedSalutation = SALUTATION_OPTIONS[selectedSalutationIdx]?.value || '';
    responseForm.salutation = formattedSalutation;

    if (isUSOrCanada.value) {
        transformCityStateDropdownValues(responseForm);
    }
};

const transformCityStateDropdownValues = (responseForm) => {
    if (isUSA.value) {
        const selectedCityIdx = parseInt(form.city);
        const formattedCity = cityDropdownOptions.value[selectedCityIdx]?.label || '';
        responseForm.city = formattedCity;
    }

    const stateOptions = isUSA.value 
        ? filteredStateOptions.value
        : stateDropdownList.value;
    const selectedStateIdx = parseInt(form.state);
    const formattedState = stateOptions[selectedStateIdx]?.value || '';
    responseForm.state = formattedState;
};

const isServerErrorNotificationVisible = ref(false);
const handleGeneralServerError = (error) => {
    if (!error) {
        return;
    }
    handleOtcState(error);
    if (error.error?.rawResponse) {
        handleRawErrorResponse(error.error.rawResponse);
    }
    else if (error.data) {
        handleErrorData(error.data);
    }
    else {
        setServerError('general', REGISTRATION_DEFAULT_ERROR_MESSAGE);
    }
    handleNotificationVisibility();
};

const handleErrorData = (response) => {
    if (!response) {
        return;
    }
    if (response.errorMessage) {
        setServerError(REG_FIELD.VERIFICATION_CODE, response.errorMessage);
        setServerError('general', REGISTRATION_FIELD_ERROR_MESSAGE);
        isAuthNotificationVisible.value = false;
    }
    else if (response.formFields 
          || response.statusMessage === REGISTRATION_REVIEW_FORM_MESSAGE) {
        setServerError('general', REGISTRATION_FIELD_ERROR_MESSAGE);
        handleResponseIsFailure(response);
    }
};

const isAllowOtcGeneration = ref(true);
const isAllowOtcEntry = ref(true);
const handleOtcState = (error) => {
    if (typeof error?.data?.isAllowOtcGeneration !== 'boolean') {
        return;
    }
    isAllowOtcGeneration.value = error.data.isAllowOtcGeneration;
    if (typeof error?.data?.isAllowOtcEntry !== 'boolean') {
        return;
    }
    isAllowOtcEntry.value = error.data.isAllowOtcEntry;
    setOtcErrorMessages();
};

const setOtcErrorMessages = () => {
    if (!isAllowOtcEntry.value) {
        setServerError('twoFactor', REGISTRATION_NO_OTC_ENTRY_ERROR_MESSAGE);
        isAuthErrorNotificationVisible.value = true;
    }
    else {
        checkResetServerError('twoFactor');
    }
    if (!isAllowOtcEntry.value && !isAllowOtcGeneration.value) {
        setServerError('twoFactor', REGISTRATION_OTC_EXIT_BROWSER_ERROR_MESSAGE);
        isAuthErrorNotificationVisible.value = true;
    }
};

const isRequiresTwoFactorValidation = computed(() =>
    isShowUsernamePassword.value && registrationInfo.value.isTwoFactorEnabled);
const handleNotificationVisibility = () => {
    if (isRequiresTwoFactorValidation.value) {
        isAuthErrorNotificationVisible.value = true;
    }
    isServerErrorNotificationVisible.value = true;
};

const handleRawErrorResponse = (error) => {
    isAuthNotificationVisible.value = false;
    setServerError('general', REGISTRATION_FIELD_ERROR_MESSAGE);
    if (error.includes(REG_FIELD.EMAIL)) {
        setServerError(REG_FIELD.EMAIL, REGISTRATION_FIELD_ERROR.INCORRECT_EMAIL);
        needsNewCode.value = true;
    }
    else if (error.includes('code')) {
        setServerError(REG_FIELD.VERIFICATION_CODE, REGISTRATION_FIELD_ERROR.INVALID_CODE);
    }
};

const handleSendVerificationCode = async({ resendCode = false }) => {
    if (isSubmitting.value) {
        return;
    }
    isSubmitting.value = true;
    const sanitizedForm = sanitizeForm({ ...form});
    if (!validateForm()) {
        isSubmitDisabled.value = true;
        isSubmitting.value = false;
        return;
    }
    if (resendCode) {
        hasResentVerificationCode.value = true;
        isAllowOtcEntry.value = true;
        checkResetServerError(REG_FIELD.VERIFICATION_CODE);
    }
    needsNewCode.value = false;
    resetAllServerErrors();
    const responseForm = setupFormPayload(sanitizedForm);
    try {
        const response = await utdRest('register/two-factor-code', { 
            formName: registrationForm.value.formName,
            ...responseForm
        });
        if (response.isSuccess) {
            isAuthErrorNotificationVisible.value = false;
            isServerErrorNotificationVisible.value = false;
            hasSentVerificationCode.value = true;
            isAuthNotificationVisible.value = true;
            sendCodeAttempts.value += 1;
        }
        else {
            handleResponseIsFailure(response);
        }
    }
    catch (error) {
        handleGeneralServerError(error);
    }
    finally {
        isSubmitting.value = false;
    }
};

const handleResponseIsFailure = (response) => {
    parseServerFieldErrors(response);
    setServerError('general', REGISTRATION_FIELD_ERROR_MESSAGE);
    isAuthErrorNotificationVisible.value = true;
    hasSentVerificationCode.value = false;
    isAuthNotificationVisible.value = false;
    isSubmitDisabled.value = true;
    isServerErrorNotificationVisible.value = true;
};

const handleFormSubmit = async() => {
    if (isSubmitting.value) {
        return;
    }
    isSubmitting.value = true;
    const sanitizedForm = sanitizeForm({ ...form});
    if (!validateForm()) {
        isSubmitting.value = false;
        return;
    }
    const responseForm = setupFormPayload(sanitizedForm);
    try {
        const response = await utdRest('register/submit', {
            form: {
                formName: registrationForm.value.formName,
                ...responseForm
            },
            twoFactorCode: form.verificationCode,
            termsAccepted: form.termsAccepted
        });
        handleSubmitResponse(response);
    }
    catch (error) {
        handleGeneralServerError(error);
    }
    finally {
        isSubmitting.value = false;
    }
};

const handleSubmitResponse = (response) => {
    if (response.isSuccess) {
        resetForm();
        const regSourceParam = getUserOriginParam();
        const url = setQueryParamValue(
            C_HTTP.DEFAULT_HOMEPAGE_PATH,
            C_REG_MERGE.REG_MERGE_SUCCESS_PARAM,
            regSourceParam
        );
        store.dispatch('login/setOidcPartnerModalShown');
        getWindow().location.href = url;
    }
    else {
        parseServerFieldErrors(response);
        setServerError('general', REGISTRATION_FIELD_ERROR_MESSAGE);
        handleNotificationVisibility();
    }
};

const getUserOriginParam = () => {
    if (store.getters['auth/isOidcUser']) {
        return C_REG_MERGE.MESSAGES.BYOL_REG.name;
    }
    return C_REG_MERGE.MESSAGES.REG.name;
};

const sanitizeForm = (requestForm) => {
    Object.keys(requestForm).forEach((key) => {
        requestForm[key] = DOMPurify.sanitize(requestForm[key]);
    });
    return requestForm;
};

const parseServerFieldErrors = (response) => {
    const fields = response?.formFields || [];
    fields.forEach((field) => {
        if (field.errorMessage) {
            setServerError(field.name, field.errorMessage);
        }
    });
};

const isOidcUser = computed(() => store.getters['auth/isOidcUser']);
const isAuthenticatedByIp = computed(() => store.getters['app/isAuthenticatedByIp']);
const router = computed(() => store.getters['app/router']);
const cancelRegistration = () => {
    resetForm();
    if (isOidcUser.value || isAuthenticatedByIp.value) {
        router.value.url(C_HTTP.DEFAULT_HOMEPAGE_PATH);
        return;
    }
    getWindow().scrollTo(0, 0);
    emit('cancelRegistration');
};

const trackRegisterBeginEvent = async() => {
    try {
        await utdRest('register/begin');
    }
    catch {
        // No-op
    }
};

onMounted(() => {
    trackRegisterBeginEvent();
});
</script>

<style lang="less" scoped>
@import (reference) '~_acaAssets/wkce/colors/wkce-app-styles';
@DS1-UTD-ERROR-ON-BLUE-BG-COLOR: @WKCE-RED-SHADE1;

@media only screen and (min-width: 768px) {
  .registration-form__container {
    .ds1-ph-2();
  }
  
  .registration-form__basic-info-container,
  .registration-form__address-info-container {
    .ds1-mh-2();
  }

  .registration-form {
    :deep(.options-card .options-card__content) {
      .ds1-pb-4();
    }

    :deep(.options-card__content .options-card__action-area) {
      .ds1-ph-5();
    }

    :deep(.registration-form__subtitle ) {
      .ds1-mh-0();
      .ds1-mt-3();
      .ds1-utd-h4();
    }
  }
}

.registration-form {
  .ds1-mt-2();
  width: 100%;

  .wk-js2 .wk-field-errored .wk-field-input:not(:hover :focus) {
    border-color: @DS1-UTD-ERROR-ON-BLUE-BG-COLOR;
  }

  :deep(.registration-form-field) {
    .ds1-ph-1();
    .ds1-ma-0();
    border: none;

    &.registration-form__terms-container {
      .ds1-ml-2();
    }
  }

  :deep(.wk-field-input.form-field) {
    background-color: @DS1-UTD-PRIMARY-BG-COLOR;  
  }

  :deep(.wk-js2 .wk-field-error) {
    color: @DS1-UTD-ERROR-ON-BLUE-BG-COLOR
  }

  :deep(.options-card__action-area) {
    .ds1-pr-1();
    gap: 24px;
  }

  :deep(.options-card__header) {
    display: block;
  }

  :deep(#backButton) {
    .ds1-pa-0();
  }
}

.registration-form__guest-pass-disclaimer {
  .ds1-mh-3();
  .ds1-mb-3();
}

.registration-form__server-error-notification {
  ul {
    .ds1-pl-3();
    .ds1-mb-0();
    .ds1-mt-1();
  }
}

#serverErrorNotification {
  .ds1-mh-3();
  .ds1-mb-2();
}

.registration-form__container {
  .wk-field-input.form-field {
    background-color: @DS1-UTD-PRIMARY-BG-COLOR;
  }
}

.registration-form__card-title {
  .ds1-ma-0();
  color: @DS1-UTD-OPTIONS-CARD-TITLE-COLOR;
  flex: 1;
}

.registration-form__privacy-policy {
  .ds1-mt-2();
  .ds1-mb-2();
  align-self: flex-start;
  
  .registration-form__privacy-policy-link {
    color: @DS1-UTD-PRIMARY-LINK-BASE-COLOR;
    
    &:hover {
      color: @WKCE-BLUE-SHADE1;
      text-decoration: underline;
    }
  }
}

.registration-form__terms-checkbox :deep(.wkce-checkbox) {
  display: inline;
}

.registration-form__terms-text {
  .registration-form__terms-link {
    color: @DS1-UTD-PRIMARY-LINK-BASE-COLOR;
    text-decoration: underline;
    
    &:hover {
      color: @WKCE-BLUE-SHADE1;
    }
  }
}
</style>